import { NextRequest, NextResponse } from 'next/server';
import { LeadHandlerConfig, LeadFormData, LeadApiResponse } from '../types';

/**
 * Create a generic NextJS API route handler for lead submission
 */
export function createLeadHandler(config: LeadHandlerConfig) {
  return async function leadHandler(request: NextRequest): Promise<NextResponse> {
    // Only allow POST requests
    if (request.method !== 'POST') {
      return NextResponse.json(
        { error: 'Method not allowed' },
        { status: 405, headers: { Allow: 'POST' } }
      );
    }

    try {
      // Parse request body
      let body: LeadFormData;
      try {
        body = await request.json();
      } catch (parseError) {
        return NextResponse.json(
          { error: 'Invalid JSON in request body' },
          { status: 400 }
        );
      }

      // Rate limiting (if configured)
      if (config.rateLimiter) {
        const allowed = await config.rateLimiter(request);
        if (!allowed) {
          return NextResponse.json(
            { error: 'Too many requests. Please try again later.' },
            { status: 429 }
          );
        }
      }

      // Validate required fields (basic validation) - support both new and legacy name fields
      const hasFirstName = Boolean(body.firstName?.length && typeof body.firstName === 'string' && body.firstName.trim().length > 0);
      const hasLegacyName = Boolean(body.name?.length && typeof body.name === 'string' && body.name.trim().length > 0);
      
      if (!hasFirstName && !hasLegacyName) {
        return NextResponse.json(
          { error: 'First name or name is required' },
          { status: 400 }
        );
      }

      if (!body.email || typeof body.email !== 'string' || body.email.trim().length === 0) {
        return NextResponse.json(
          { error: 'Email is required' },
          { status: 400 }
        );
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(body.email.trim())) {
        return NextResponse.json(
          { error: 'Please enter a valid email address' },
          { status: 400 }
        );
      }

      // Custom validation (if configured)
      if (config.validation) {
        for (const [field, rules] of Object.entries(config.validation)) {
          let value = (body as any)[field];
          
          // Special handling for 'name' field when using firstName/lastName
          if (field === 'name' && !value && hasFirstName) {
            // Use combined firstName + lastName for name validation if legacy name field is being validated
            const firstName = body.firstName?.trim() || '';
            const lastName = body.lastName?.trim() || '';
            value = `${firstName} ${lastName}`.trim();
          }
          
          for (const rule of rules) {
            let isValid = true;
            let errorMessage = rule.message || 'Validation failed';

            switch (rule.type) {
              case 'required':
                // Special handling for 'name' field
                if (field === 'name') {
                  isValid = hasFirstName || hasLegacyName;
                } else {
                  isValid = value != null && value !== '' && 
                           (typeof value !== 'string' || value.trim() !== '');
                }
                break;
              
              case 'email':
                if (value) {
                  isValid = emailRegex.test(String(value).trim());
                }
                break;
              
              case 'minLength':
                if (value && typeof rule.value === 'number') {
                  isValid = String(value).length >= rule.value;
                }
                break;
              
              case 'maxLength':
                if (value && typeof rule.value === 'number') {
                  isValid = String(value).length <= rule.value;
                }
                break;
              
              case 'pattern':
                if (value && typeof rule.value === 'string') {
                  const regex = new RegExp(rule.value);
                  isValid = regex.test(String(value));
                }
                break;
              
              case 'custom':
                if (rule.validator) {
                  const result = rule.validator(value);
                  isValid = result === true;
                  if (typeof result === 'string') {
                    errorMessage = result;
                  }
                }
                break;
            }

            if (!isValid) {
              return NextResponse.json(
                { error: errorMessage },
                { status: 400 }
              );
            }
          }
        }
      }

      // reCAPTCHA verification (if configured)
      if (config.recaptcha && (body as any).token) {
        const { verifyRecaptcha, getRecaptchaErrorMessage } = await import('./recaptcha');
        const recaptchaResult = await verifyRecaptcha((body as any).token, config.recaptcha);

        if (!recaptchaResult.success) {
          const errorMessage = getRecaptchaErrorMessage(recaptchaResult.errorCodes || []);
          return NextResponse.json(
            { error: `reCAPTCHA verification failed: ${errorMessage}` },
            { status: 400 }
          );
        }

        // Remove token from data before sending to API
        delete (body as any).token;
      }

      // Call the user-provided submit function
      let response: LeadApiResponse;
      try {
        response = await config.onSubmit(body);
      } catch (submitError) {
        console.error('Lead submission error:', submitError);
        
        // Call error callback if provided
        if (config.onError) {
          await config.onError(body, submitError instanceof Error ? submitError : new Error(String(submitError)));
        }

        return NextResponse.json(
          { error: 'Failed to submit lead. Please try again.' },
          { status: 500 }
        );
      }

      // Validate response from submit function
      if (!response || typeof response !== 'object') {
        console.error('Invalid response from submit function:', response);
        return NextResponse.json(
          { error: 'Internal server error' },
          { status: 500 }
        );
      }

      // Handle unsuccessful submission
      if (!response.success) {
        const statusCode = response.message?.includes('validation') ? 400 : 500;
        return NextResponse.json(
          { 
            error: response.message || 'Failed to submit lead',
            data: response.data 
          },
          { status: statusCode }
        );
      }

      // Call success callback if provided
      if (config.onSuccess) {
        try {
          await config.onSuccess(body, response);
        } catch (callbackError) {
          console.error('Success callback error:', callbackError);
          // Don't fail the request if callback fails
        }
      }

      // Return successful response
      return NextResponse.json({
        success: true,
        message: response.message || 'Lead submitted successfully',
        id: response.id,
        data: response.data
      });

    } catch (error) {
      console.error('Unexpected error in lead handler:', error);
      
      // Try to call error callback
      if (config.onError) {
        try {
          await config.onError({} as LeadFormData, error instanceof Error ? error : new Error(String(error)));
        } catch (callbackError) {
          console.error('Error callback failed:', callbackError);
        }
      }

      return NextResponse.json(
        { error: 'Internal server error' },
        { status: 500 }
      );
    }
  };
}

/**
 * Helper function to create CORS headers for the response
 */
export function createCorsHeaders(allowedOrigins?: string[]): HeadersInit {
  const headers: HeadersInit = {
    'Access-Control-Allow-Methods': 'POST, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
  };

  if (allowedOrigins && allowedOrigins.length > 0) {
    headers['Access-Control-Allow-Origin'] = allowedOrigins.join(', ');
  } else {
    headers['Access-Control-Allow-Origin'] = '*';
  }

  return headers;
}

/**
 * Helper to handle OPTIONS requests for CORS
 */
export function handleOptions(allowedOrigins?: string[]): NextResponse {
  return new NextResponse(null, {
    status: 200,
    headers: createCorsHeaders(allowedOrigins)
  });
}